/**
 * @file uploadAutomationServices.js
 * @description API calls for Configuration → Upload Automation.
 *
 *              The API speaks snake_case (see uploadAutomationConfig.js);
 *              the components speak camelCase. mapConfigFromApi and
 *              mapConfigToApi are the only place those two meet, so a key
 *              rename on the backend is a one-file change here.
 */

// ⚠ ONE LINE TO CHANGE: point this at your shared axios instance.
//   Open src/services/reportModule/reportServices.js and copy its
//   apiClient import verbatim — this file must resolve it the same way.
//   From src/services/uploadAutomationModule/ the common candidates are:
//       import apiClient from "../apiInstance.js"";
//       import apiClient from "../../common/apiClient";
//       import apiClient from "../../utils/apiClient";
import apiClient from "../apiInstance.js";

const BASE = "/uploadAutomations";

// ── Mappers ────────────────────────────────────────────────────────
export const mapConfigFromApi = (row) => ({
    configId:         row.config_id,
    name:             row.name || "",
    description:      row.description || "",
    language:         row.language,
    uploadOrder:      row.upload_order,
    campaignId:       row.campaign_id,
    campaignName:     row.campaign_name,
    fileNameExample:  row.file_name_example || "",
    fileNameTemplate: row.file_name_template,
    fileNamePattern:  row.file_name_pattern,
    fileNameTokens:   row.file_name_tokens,
    fileExtension:    row.file_extension,
    days:             row.days || [],
    startTime:        row.start_time,
    endTime:          row.end_time,
    scheduleIds:      row.schedule_ids || [],
    schedules:        (row.schedules || []).map(s => ({
        scheduleId:   s.schedule_id,
        scheduleName: s.schedule_name,
        isActive:     !!s.is_active,
    })),
    isEnabled:        !!row.is_enabled,
    notifyEnabled:    !!row.notify_enabled,
    notifyRecipients: row.notify_recipients || [],
    lastRunDate:      row.last_run_date,
    lastRunStatus:    row.last_run_status,
    lastFileName:     row.last_file_name,
    nextRunDate:      row.next_run_date,
    nextFileName:     row.next_file_name,
    timeZoneKey:      row.time_zone_key,
});

export const mapConfigToApi = (draft) => ({
    name:              draft.name,
    description:       draft.description,
    language:          draft.language,
    upload_order:      Number(draft.uploadOrder),
    campaign_id:       Number(draft.campaignId),
    file_name_example: draft.fileNameExample,
    days:              draft.days,
    start_time:        draft.startTime,
    end_time:          draft.endTime,
    schedule_ids:      draft.scheduleIds,
    is_enabled:        !!draft.isEnabled,
    notify_enabled:    !!draft.notifyEnabled,
    notify_recipients: draft.notifyRecipients || [],
});

const mapCampaignFromApi = (row) => ({
    campaignId:          row.campaign_id,
    campaignName:        row.campaign_name,
    description:         row.description || "",
    language:            row.language,
    category:            row.category,
    activeScheduleCount: row.active_schedule_count,
});

const mapCampaignScheduleFromApi = (row) => ({
    scheduleId:   row.schedule_id,
    campaignId:   row.campaign_id,
    scheduleName: row.schedule_name,
    startdate:    row.startdate,
    enddate:      row.enddate,
    isActive:     !!row.is_active,
    days:         row.days || [],
    times:        row.times || {},
});

const mapFileFromApi = (row) => ({
    fileId:         row.file_id,
    configId:       row.config_id,
    fileName:       row.file_name,
    blobPath:       row.blob_path,
    fileSize:       row.file_size,
    runDate:        row.run_date,
    sequenceNo:     row.sequence_no,
    status:         row.status,
    attemptCount:   row.attempt_count,
    claimedAt:      row.claimed_at,
    claimedBy:      row.claimed_by,
    completedAt:    row.completed_at,
    rowCountLoaded: row.row_count,
    errorMessage:   row.error_message,
});

// ── Calls ──────────────────────────────────────────────────────────
export const fetchAutomationConfigs = async (params = {}) => {
    const { data } = await apiClient.get(BASE, { params });
    return (data || []).map(mapConfigFromApi);
};

export const fetchAutomationConfig = async (configId) => {
    const { data } = await apiClient.get(`${BASE}/${configId}`);
    return data ? mapConfigFromApi(data) : null;
};

/** Active campaigns, optionally narrowed to one language. */
export const fetchAutomationCampaigns = async (language) => {
    const { data } = await apiClient.get(`${BASE}/campaigns`, {
        params: language ? { language } : {},
    });
    return (data || []).map(mapCampaignFromApi);
};

/** campaigntextmsg rows belonging to one campaign. */
export const fetchCampaignSchedules = async (campaignId) => {
    const { data } = await apiClient.get(`${BASE}/campaigns/${campaignId}/schedules`);
    return (data || []).map(mapCampaignScheduleFromApi);
};

/**
 * Server-side read of a file-name example. The dialog previews locally with
 * the same module, so this is only needed to confirm parity.
 */
export const previewFileFormat = async (fileNameExample) => {
    const { data } = await apiClient.post(`${BASE}/preview`, {
        file_name_example: fileNameExample,
    });
    return data;
};

/** Create when configId is null, update otherwise. Returns the saved row. */
export const saveAutomationConfig = async (draft) => {
    const body = mapConfigToApi(draft);
    const { data } = draft.configId
        ? await apiClient.put(`${BASE}/${draft.configId}`, body)
        : await apiClient.post(BASE, body);
    return data ? mapConfigFromApi(data) : null;
};

export const toggleAutomationConfig = async (configId, isEnabled) => {
    const { data } = await apiClient.patch(`${BASE}/${configId}/enabled`, {
        is_enabled: isEnabled,
    });
    return data ? mapConfigFromApi(data) : null;
};

export const deleteAutomationConfig = async (configId) => {
    const { data } = await apiClient.delete(`${BASE}/${configId}`);
    return data;
};

/** Recent files the tracker has claimed for one automation. */
export const fetchAutomationFiles = async (configId, limit = 50) => {
    const { data } = await apiClient.get(`${BASE}/${configId}/files`, { params: { limit } });
    return (data || []).map(mapFileFromApi);
};

/** Reuses the report module's client time-zone endpoint. */
export { fetchClientTimeZone } from "../reportModule/reportServices.js";